// deno-lint-ignore-file ban-types no-explicit-any
/**
 * @module
 * Handles the actual XRPC client functionalities.
 */ export var ResponseType;
(function(ResponseType) {
  /** Unknown error from the library */ ResponseType[ResponseType["Unknown"] = 1] = "Unknown";
  /** The server returned an invalid response */ ResponseType[ResponseType["InvalidResponse"] = 2] = "InvalidResponse";
  /** Successful response from the service */ ResponseType[ResponseType["Success"] = 200] = "Success";
  /** Request was considered invalid by the service */ ResponseType[ResponseType["InvalidRequest"] = 400] = "InvalidRequest";
  /** Service requires an authentication token */ ResponseType[ResponseType["AuthRequired"] = 401] = "AuthRequired";
  /** Request is forbidden by the service */ ResponseType[ResponseType["Forbidden"] = 403] = "Forbidden";
  /** Not a XRPC service */ ResponseType[ResponseType["XRPCNotSupported"] = 404] = "XRPCNotSupported";
  /** Payload is considered too large by the service */ ResponseType[ResponseType["PayloadTooLarge"] = 413] = "PayloadTooLarge";
  /** Ratelimit was exceeded */ ResponseType[ResponseType["RateLimitExceeded"] = 429] = "RateLimitExceeded";
  /** Internal server error */ ResponseType[ResponseType["InternalServerError"] = 500] = "InternalServerError";
  /** Method hasn't been implemented */ ResponseType[ResponseType["MethodNotImplemented"] = 501] = "MethodNotImplemented";
  /** Failure by an upstream service */ ResponseType[ResponseType["UpstreamFailure"] = 502] = "UpstreamFailure";
  /** Not enough resources */ ResponseType[ResponseType["NotEnoughResouces"] = 503] = "NotEnoughResouces";
  /** Timeout from upstream service */ ResponseType[ResponseType["UpstreamTimeout"] = 504] = "UpstreamTimeout";
})(ResponseType || (ResponseType = {}));
/** XRPC response status which are recoverable (network error) */ export const RECOVERABLE_RESPONSE_STATUS = [
  1,
  408,
  425,
  429,
  500,
  502,
  503,
  504,
  522,
  524
];
/** Error coming from the XRPC service */ export class XRPCError extends Error {
  name = 'XRPCError';
  /** Response status */ status;
  /** Response headers */ headers;
  /** Error kind */ kind;
  constructor(status, { kind, message, headers, cause } = {}){
    super(message || `Unspecified error message`, {
      cause
    });
    this.status = status;
    this.kind = kind;
    this.headers = headers || {};
  }
}
/** The client that sends out requests. */ export class XRPC {
  /** The service it should connect to */ service;
  /** XRPC fetch handler */ fetch = fetchHandler;
  constructor(options){
    this.service = options.service;
  }
  /**
	 * Adds a hook to intercept XRPC requests.
	 * Hooks are executed from last-registered to first-registered
	 * @param fn Hook function
	 */ hook(fn) {
    this.fetch = fn(this.fetch);
  }
  /**
	 * Makes a query (GET) request
	 * @param nsid Namespace ID of a query endpoint
	 * @param options Options to include like parameters
	 * @returns The response of the request
	 */ get(nsid, options) {
    return this.#call({
      type: 'get',
      nsid: nsid,
      ...options
    });
  }
  /**
	 * Makes a procedure (POST) request
	 * @param nsid Namespace ID of a procedure endpoint
	 * @param options Options to include like input body or parameters
	 * @returns The response of the request
	 */ call(nsid, options) {
    return this.#call({
      type: 'post',
      nsid: nsid,
      ...options
    });
  }
  async #call(request) {
    const { status, headers, body } = await this.fetch({
      ...request,
      service: this.service,
      headers: request.headers === undefined ? {} : request.headers,
      params: request.params === undefined ? {} : request.params
    });
    if (status === ResponseType.Success) {
      return {
        data: body,
        headers: headers
      };
    } else if (isErrorResponse(body)) {
      throw new XRPCError(status, {
        kind: body.error,
        message: body.message,
        headers
      });
    } else {
      throw new XRPCError(status, {
        headers
      });
    }
  }
}
/**
 * Clones an XRPC instance
 * @param rpc Base instance
 * @returns The cloned instance
 */ export const clone = (rpc)=>{
  const cloned = new XRPC({
    service: rpc.service
  });
  cloned.fetch = rpc.fetch;
  return cloned;
};
/**
 * Clones an existing XRPC instance, with a proxy on top.
 * @param rpc Base instance
 * @param opts Proxying options
 * @returns Cloned instance with a proxy added
 */ export const withProxy = (rpc, opts)=>{
  const cloned = clone(rpc);
  cloned.hook((next)=>(request)=>{
      return next({
        ...request,
        headers: {
          ...request.headers,
          'atproto-proxy': `${opts.service}#${opts.type}`
        }
      });
    });
  return cloned;
};
/** Default fetch handler */ export const fetchHandler = async ({ service, type, nsid, headers, params, encoding, data: input, signal })=>{
  const uri = new URL(`/xrpc/${nsid}`, service);
  const searchParams = uri.searchParams;
  for(const key in params){
    const value = params[key];
    if (value !== undefined) {
      if (Array.isArray(value)) {
        for(let idx = 0, len = value.length; idx < len; idx++){
          const val = value[idx];
          searchParams.append(key, val);
        }
      } else {
        searchParams.set(key, value);
      }
    }
  }
  const isProcedure = type === 'post';
  const isJson = typeof input === 'object' && !(input instanceof FormData || input instanceof Blob || ArrayBuffer.isView(input));
  const response = await fetch(uri, {
    signal: signal,
    method: isProcedure ? 'POST' : 'GET',
    headers: encoding || isJson ? {
      ...headers,
      'Content-Type': encoding || 'application/json'
    } : headers,
    body: isJson ? JSON.stringify(input) : input
  });
  const responseHeaders = response.headers;
  const responseType = responseHeaders.get('Content-Type');
  let promise;
  let data;
  if (responseType) {
    if (responseType.startsWith('application/json')) {
      promise = response.json();
    } else if (responseType.startsWith('text/')) {
      promise = response.text();
    }
  }
  try {
    data = await (promise || response.arrayBuffer().then((buffer)=>new Uint8Array(buffer)));
  } catch (err) {
    throw new XRPCError(ResponseType.InvalidResponse, {
      cause: err,
      message: `Failed to parse response body`
    });
  }
  return {
    status: response.status,
    headers: Object.fromEntries(responseHeaders),
    body: data
  };
};
/**
 * Check if provided value is an error object
 * @param value Response value
 * @param names If provided, also checks if the error name matches what you expect
 * @returns A boolean on the check
 */ export const isErrorResponse = (value, names)=>{
  if (typeof value !== 'object' || !value) {
    return false;
  }
  const kindType = typeof value.error;
  const messageType = typeof value.message;
  return (kindType === 'undefined' || kindType === 'string') && (messageType === 'undefined' || messageType === 'string') && (!names || names.includes(value.error));
};
//# sourceMappingURL=xrpc.js.map